/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty, jsonToSxProps, cssToSxProps, ifNotTransparent, ifNotNull, colorExists, validateString } from '@olympeio/core';
import { useOlympeTheme } from '../mui';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import MUIButton from '@mui/material/Button';
import Icon from '@mui/material/Icon';



/**
 * Provide a Button visual component using MUI Button
 */
export default class Button extends ReactBrick {
    /**
     * @override
     */
    setupExecution($: BrickContext) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    updateParent(parent: HTMLElement, element: HTMLElement) {
        parent.style.overflow = 'visible'; // Allow overflow
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($: BrickContext) {
        return (props: { values: [hidden: boolean] }) => {
            const [hidden] = props.values;
            const theme = useOlympeTheme($);

            const color = useProperty($, 'color', true);
            const startIcon = useProperty($, 'start icon', true);
            const endIcon = useProperty($, 'end icon', true);
            const borderColor = useProperty($, 'Border Color', true);
            const borderRadius = useProperty($, 'Border Radius', true);
            const borderWidth = useProperty($, 'Border Width', true);
            const defaultColor = useProperty($, 'Default Color', true);

            const customSx = {
                ...ifNotTransparent('color', useProperty($, 'text color', true)),
                ...ifNotTransparent('borderColor', borderColor),
                ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                ...ifNotNull('borderWidth', borderWidth),
                ...ifNotTransparent('borderStyle', 'solid', borderColor),
                ...ifNotTransparent('backgroundColor', defaultColor),
                '&:hover': {
                    ...ifNotTransparent('borderColor', borderColor),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotNull('borderWidth', borderWidth),
                    ...ifNotTransparent('borderStyle', 'solid', borderColor),
                    ...ifNotTransparent('backgroundColor', defaultColor),
                },
            };

            return (
                !hidden && (
                    <ThemeProvider theme={theme}>
                        <MUIButton
                            variant={useProperty($, 'variant', true)}
                            color={colorExists(theme, color, 'Button') ? color : 'primary'}
                            startIcon={validateString(startIcon, 'start icon', 'Button') && <Icon>{startIcon}</Icon>}
                            endIcon={validateString(endIcon, 'end icon', 'Button') && <Icon>{endIcon}</Icon>}
                            disabled={useProperty($, 'disabled', true)}
                            tabIndex={useProperty($, 'Tab Index', true)}
                            onClick={() => $.trigger('On Click')}
                            sx={{
                                width: 1,
                                height: 1,
                                ...customSx,
                                boxSizing: 'border-box',
                                borderRadius: '40px',
                                ...cssToSxProps(useProperty($, 'CSS Property', true)),
                                ...jsonToSxProps(useProperty($, 'mui sx', true)),
                            }}
                        >
                            ({validateString(useProperty($, 'text', true), 'Text', 'Button') ?? 'New Button'})
                        </MUIButton>
                    </ThemeProvider>
                )
            );
        };
    }
}

registerBrick('01917585b267d9ef6672', Button);
